import React, { useState, useContext, useEffect } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'
import { useDashboardCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useDashboardCalculations'
import styled from 'styled-components'

// @mui material components
import Grid from '@mui/material/Grid'
import HelpIcon from '@mui/icons-material/Help'
import Typography from '@mui/material/Typography'

// Soft UI Dashboard PRO React components
import Box from '@mui/material/Box'
import SoftTypography from 'components/SoftTypography'

// Wizard application components
import FormField from 'components/FormField'
import CurrencyField from 'components/FormField/CurrencyField'
import PhoneNumberField from 'components/FormField/PhoneNumberField'
import { PropTypes } from 'prop-types'
import SoftSelect from 'components/SoftSelect'
import { IconButton, Tooltip, Fade, CircularProgress } from '@mui/material'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import parcoDog from 'assets/images/parcoDog.svg'
import financeImage from 'assets/images/finance-image.svg'
import { ClockIcon } from '@mui/x-date-pickers'

import family from 'assets/images/onboarding/family.png'
import paycheck from 'assets/images/onboarding/paystub.png'
import deductions from 'assets/images/onboarding/deductions.png'
import outro from 'assets/images/onboarding/outro.png'
import fegli from 'assets/images/onboarding/fegli.png'
import survivor from 'assets/images/onboarding/survivor.png'
import user from 'assets/images/onboarding/user.png'
import create from 'assets/images/onboarding/create.png'
import dashboard from 'assets/images/onboarding/dashboard.png'
import privateLifeInsurance from 'assets/images/onboarding/lifeinsurance.png'
import UserProfileTile from 'components/UserProfileTile'

const WizardStep = ({
  id,
  label,
  placeholder = '',
  inputType = 'text',
  header = '',
  subHeader = '',
  preHeader = '',
  helpText = '',
  time = '',
  options = {},
  extraContent = '',
  required = false,
  handleNext,
  image = true,
  override = false,
  fieldErrors,
  setFieldErrors,
  loaderIcon,
  score,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const placeholderText =
    typeof placeholder === 'function' ? placeholder(intakeState) : placeholder
  const labelText = typeof label === 'function' ? label(intakeState) : label
  const helpTextMod =
    typeof helpText === 'function' ? helpText(intakeState) : helpText

  //map of the images because there are only a few sections for now
  const interstitialImageMap = {
    interstitialFamily: family,
    interstitialFinalSteps: outro,
    interstitialGrabYourPaystub: paycheck,
    interstitialStartWithDeductions: deductions,
    interstitialStartWithTSP: deductions,
    interstitialFEGLI: fegli,
    interstitialSurvivor: survivor,
    interstitialExplore: create,
    interstitialHeadToDashboard: dashboard,
    interstitialContinueOnboarding: create,
    interstitialPrivateLifeInsurance: privateLifeInsurance,
  }

  const getInterstitialImage = () => {
    const idFromSearch = new URLSearchParams(window.location.search).get(
      'onboardingStep'
    )
    return interstitialImageMap[idFromSearch] || user
  }

  useEffect(() => {
    if (!loaderIcon) return
    const timer = setTimeout(() => {
      handleNext()
    }, 3000) // 3 seconds

    return () => clearTimeout(timer) // cleanup on unmount
  }, [handleNext])

  return (
    <Box sx={{ textAlign: 'left' }} pb={3}>
      {!override && (
        <>
          <Box>
            <Typography
              variant="subtitle1"
              fontSize={{ xs: '16px', sm: '20px' }}
            >
              {preHeader || ''}
            </Typography>
          </Box>
          <Box mb={1}>
            <Typography variant="h4" color={'#262627'}>
              {header || "Let's start with your basic information:"}
            </Typography>
          </Box>
          <Typography variant="body1" color={'#495057'}>
            {subHeader ||
              'Let us know your name and other basic details about you!'}
          </Typography>
          {time && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '18px',
                mt: 1,
              }}
            >
              <ClockIcon />
              <Typography
                sx={{ marginLeft: '4px', textAlign: 'center' }}
                variant="body3Light"
                color={'#495057'}
              >
                {time}
              </Typography>
            </Box>
          )}
        </>
      )}

      {inputType == 'displayOnly' && (
        <>
          {loaderIcon ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress sx={{ color: '#2370ED' }} />
            </Box>
          ) : (
            <Box
              sx={{
                width: { xs: extraContent ? '100%' : 250, sm: '100%' },
                margin: 'auto',
              }}
              mt={3}
            >
              <div style={{ marginTop: '24px' }}>
                {extraContent && extraContent}
                {image && !score && (
                  <>
                    {getInterstitialImage() === user ? (
                      <UserProfileTile />
                    ) : (
                      <Box
                        component="img"
                        src={getInterstitialImage()}
                        sx={{
                          width: '100%',
                          maxWidth: { xs: 250, sm: 350 },
                          display: 'block',
                          mx: 'auto',
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </Box>
          )}
        </>
      )}
      <Box mt={2}>
        {inputType !== 'displayOnly' && (
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12}>
              {/* <SoftTypography
                component="label"
                variant="body2"
                fontWeight="normal"
                textTransform="capitalize"
              >
                {helpTextMod ? (
                  <span>
                    {labelText}
                    {required && <span style={{ color: 'red' }}> *</span>}
                    <Tooltip
                      TransitionComponent={Fade}
                      title={helpTextMod}
                      placement="top"
                      color="info"
                    >
                      <IconButton size="small" color="dark">
                        <HelpIcon sx={{ marginTop: '-6px' }} />
                      </IconButton>
                    </Tooltip>
                  </span>
                ) : (
                  <>
                    {labelText}
                    {required && <span style={{ color: 'red' }}>*</span>}
                  </>
                )}
              </SoftTypography> */}
              {renderMuiFormField({
                inputType,
                id,
                placeholder: placeholderText,
                label: labelText,
                value: intakeState?.[id],
                onChange: (submitValue) =>
                  updateIntakeStateAtKey({
                    [id]: submitValue,
                  }),
                options,
                handleNext,
              })}
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  )
}

WizardStep.displayName = 'WizardStep'
export default WizardStep
